/**
 * PDF Export Service
 * Handles exporting PDFs with burned-in annotations using pdf-lib
 */
import { PDFDocument, rgb } from "pdf-lib"

class PdfExportService {
  constructor() {
    this.embeddedImageCache = new Map()
  }

  /**
   * Main export function - fetches PDF, burns in annotations, returns Blob
   * @param {string} pdfUrl - URL to fetch the original PDF
   * @param {Array} annotations - Array of annotation objects
   * @param {string} originalFilename - Original PDF filename
   * @param {number} scale - Current canvas scale (default 1.0)
   * @returns {Promise<Blob>} - Modified PDF as Blob
   */
  async exportPdfWithAnnotations(pdfUrl, annotations, originalFilename, scale = 1.0) {
    try {
      // eslint-disable-next-line no-console
      console.log("Starting PDF export with annotations:", {
        pdfUrl,
        annotationCount: annotations.length,
        originalFilename,
        scale
      })

      this.embeddedImageCache.clear()

      // 1. Fetch original PDF
      const pdfBytes = await this.fetchPdfAsArrayBuffer(pdfUrl)

      // 2. Load PDF with pdf-lib
      const pdfDoc = await PDFDocument.load(pdfBytes)
      const pages = pdfDoc.getPages()

      // 3. Group annotations by page
      const annotationsByPage = this.groupAnnotationsByPage(annotations)

      // 4. Apply annotations to each page
      // eslint-disable-next-line no-restricted-syntax
      for (const [pageNum, pageAnnotations] of Object.entries(annotationsByPage)) {
        const pageIndex = parseInt(pageNum, 10) - 1
        if (pageIndex >= 0 && pageIndex < pages.length) {
          const page = pages[pageIndex]
          const pageHeight = page.getHeight()
          const pageWidth = page.getWidth()

          // eslint-disable-next-line no-console
          console.log(`Processing page ${pageNum} with ${pageAnnotations.length} annotations`, {
            pageHeight,
            pageWidth
          })

          // eslint-disable-next-line no-restricted-syntax
          for (const annotation of pageAnnotations) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await this.burnAnnotationToPdfPage(pdfDoc, page, annotation, pageHeight, scale)
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(`Failed to burn annotation ${annotation.id}:`, err)
              // Continue with other annotations
            }
          }
        }
      }

      // 5. Save and return modified PDF
      const modifiedPdfBytes = await pdfDoc.save()
      return new Blob([modifiedPdfBytes], { type: "application/pdf" })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("PDF export failed:", error)
      throw new Error(`Failed to export PDF: ${error.message}`)
    }
  }

  /**
   * Fetch PDF as ArrayBuffer from URL
   */
  async fetchPdfAsArrayBuffer(url) {
    // Add authentication if needed (gon.auth_token)
    const headers = {}
    if (typeof gon !== "undefined" && gon.auth_token) {
      headers.Authorization = gon.auth_token
    }

    const response = await fetch(url, { headers })

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.statusText}`)
    }

    return response.arrayBuffer()
  }

  async fetchResourceAsArrayBuffer(url) {
    if (!url) {
      throw new Error("Resource URL is required")
    }

    const fetchOptions = {}
    const shouldAuthorize = this.shouldAttachAuthHeader(url)
    if (shouldAuthorize && typeof gon !== "undefined" && gon.auth_token) {
      fetchOptions.headers = { Authorization: gon.auth_token }
    }

    const response = await fetch(url, fetchOptions)
    if (!response.ok) {
      throw new Error(`Failed to fetch resource: ${response.statusText}`)
    }

    return response.arrayBuffer()
  }

  shouldAttachAuthHeader(url) {
    if (!url) return false
    const isAbsolute = /^https?:\/\//i.test(url)
    if (!isAbsolute) {
      return true
    }
    if (typeof window === "undefined") {
      return false
    }
    return url.startsWith(window.location.origin)
  }

  /**
   * Group annotations by page number
   */
  groupAnnotationsByPage(annotations) {
    return annotations.reduce((acc, ann) => {
      const pageNum = ann.page || 1
      if (!acc[pageNum]) acc[pageNum] = []
      acc[pageNum].push(ann)
      return acc
    }, {})
  }

  /**
   * Burn a single annotation onto a PDF page
   */
  async burnAnnotationToPdfPage(pdfDoc, page, annotation, pageHeight, scale) {
    switch (annotation.type) {
      case "highlight":
        this.drawHighlight(page, annotation.data, pageHeight, scale)
        break
      case "text":
        this.drawText(page, annotation.data, pageHeight, scale)
        break
      case "signature":
        this.drawSignature(page, annotation.data, pageHeight, scale)
        break
      case "signature_image":
        await this.drawSignatureImage(pdfDoc, page, annotation.data, pageHeight, scale)
        break
      case "stamp":
        this.drawStamp(page, annotation.data, pageHeight, scale)
        break
      case "line":
        this.drawLineShape(page, annotation.data, pageHeight, scale)
        break
      case "rectangle":
        this.drawRectangleShape(page, annotation.data, pageHeight, scale)
        break
      case "circle":
        this.drawEllipseShape(page, annotation.data, pageHeight, scale)
        break
      case "ellipse":
        this.drawEllipseShape(page, annotation.data, pageHeight, scale)
        break
      case "signature_block":
        await this.drawSignatureBlock(pdfDoc, page, annotation.data, pageHeight, scale)
        break
      default:
        // eslint-disable-next-line no-console
        console.warn(`Unknown annotation type: ${annotation.type}`)
    }
  }

  /**
   * Draw highlight rectangle
   */
  drawHighlight(page, data, pageHeight, scale) {
    const {
      x, y, width, height, color, opacity
    } = data

    // Transform coordinates from canvas to PDF
    // Canvas uses 1.5x scale, so we divide by scale to get PDF coordinates
    // PDF coordinate system has origin at bottom-left, canvas at top-left
    const pdfX = x / scale
    const pdfY = pageHeight - ((y + height) / scale)
    const pdfWidth = width / scale
    const pdfHeight = height / scale

    // eslint-disable-next-line no-console
    console.log("Drawing highlight:", {
      canvas: {
        x, y, width, height
      },
      pdf: {
        x: pdfX, y: pdfY, width: pdfWidth, height: pdfHeight
      },
      pageHeight,
      scale
    })

    const fillOpacity = typeof opacity === "number" ? opacity : 0.4

    page.drawRectangle({
      x: pdfX,
      y: pdfY,
      width: pdfWidth,
      height: pdfHeight,
      color: this.colorStringToRgb(color || "yellow"),
      opacity: fillOpacity
    })
  }

  /**
   * Draw text annotation
   */
  drawText(page, data, pageHeight, scale) {
    const {
      x, y, text, fontSize, color, fontWeight
    } = data

    // Transform coordinates from canvas to PDF
    // Adjust for text baseline
    const pdfX = x / scale
    const pdfY = pageHeight - ((y + (fontSize || 16)) / scale)

    const fontColor = this.colorStringToRgb(color || "red")
    const renderSize = (fontSize || 16) / scale
    const textValue = text || ""
    const weight = (fontWeight || "normal").toString().toLowerCase()

    try {
      const drawOptions = {
        x: pdfX,
        y: pdfY,
        size: renderSize,
        color: fontColor
      }

      page.drawText(textValue, drawOptions)

      if (weight === "bold") {
        page.drawText(textValue, {
          ...drawOptions,
          x: pdfX + (0.4 / scale)
        })
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to draw text:", err)
      // Try with default font if custom font fails
      page.drawText(textValue, {
        x: pdfX,
        y: pdfY,
        size: 12,
        color: fontColor
      })
    }
  }

  /**
   * Draw signature as connected lines
   */
  drawSignature(page, data, pageHeight, scale) {
    const { path, color, lineWidth } = data

    if (!path || path.length < 2) {
      // eslint-disable-next-line no-console
      console.warn("Signature path is too short")
      return
    }

    const lineColor = this.colorStringToRgb(color || "blue")
    const thickness = (lineWidth || 2) / scale

    // Draw lines between consecutive points
    for (let i = 0; i < path.length - 1; i += 1) {
      const start = path[i]
      const end = path[i + 1]

      // Transform coordinates
      const startX = start.x / scale
      const startY = pageHeight - (start.y / scale)
      const endX = end.x / scale
      const endY = pageHeight - (end.y / scale)

      page.drawLine({
        start: { x: startX, y: startY },
        end: { x: endX, y: endY },
        thickness,
        color: lineColor,
        opacity: 1.0
      })
    }
  }

  async drawSignatureImage(pdfDoc, page, data, pageHeight, scale) {
    if (!data?.imageUrl) {
      return
    }

    const cacheKey = data.imageUrl
    let embeddedImage = this.embeddedImageCache.get(cacheKey)
    if (!embeddedImage) {
      const imageBytes = await this.fetchResourceAsArrayBuffer(data.imageUrl)
      try {
        embeddedImage = await pdfDoc.embedPng(imageBytes)
      } catch (pngErr) {
        embeddedImage = await pdfDoc.embedJpg(imageBytes)
      }
      this.embeddedImageCache.set(cacheKey, embeddedImage)
    }

    const renderWidth = data.width || embeddedImage.width
    const renderHeight = data.height || embeddedImage.height
    const pdfWidth = renderWidth / scale
    const pdfHeight = renderHeight / scale
    const pdfX = data.x / scale
    const pdfY = pageHeight - ((data.y + renderHeight) / scale)

    page.drawImage(embeddedImage, {
      x: pdfX,
      y: pdfY,
      width: pdfWidth,
      height: pdfHeight
    })
  }

  /**
   * Draw signature block annotation (signature + name + title + surveyor ID)
   */
  async drawSignatureBlock(pdfDoc, page, data, pageHeight, scale) {
    const {
      x, y, width, signatureImageUrl, userName, userTitle, surveyorId,
      showSignatureLine, fontSize = 12
    } = data

    const pdfX = x / scale
    const signatureHeight = 50 / scale
    const blockWidth = (width || 200) / scale
    const textFontSize = fontSize / scale
    const lineHeight = textFontSize * 1.4

    // Start Y position - PDF uses bottom-left origin
    let currentPdfY = pageHeight - (y / scale)

    // Draw signature image if available
    if (signatureImageUrl) {
      try {
        let embeddedImage = this.embeddedImageCache.get(signatureImageUrl)
        if (!embeddedImage) {
          const imageBytes = await this.fetchResourceAsArrayBuffer(signatureImageUrl)
          try {
            embeddedImage = await pdfDoc.embedPng(imageBytes)
          } catch (pngErr) {
            embeddedImage = await pdfDoc.embedJpg(imageBytes)
          }
          this.embeddedImageCache.set(signatureImageUrl, embeddedImage)
        }

        page.drawImage(embeddedImage, {
          x: pdfX,
          y: currentPdfY - signatureHeight,
          width: blockWidth,
          height: signatureHeight
        })
        currentPdfY -= signatureHeight + (5 / scale)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to embed signature image in block:", err)
        // Fall back to signature line
        if (showSignatureLine) {
          this.drawSignatureLine(page, pdfX, currentPdfY, blockWidth, signatureHeight)
          currentPdfY -= signatureHeight + (5 / scale)
        }
      }
    } else if (showSignatureLine) {
      this.drawSignatureLine(page, pdfX, currentPdfY, blockWidth, signatureHeight)
      currentPdfY -= signatureHeight + (5 / scale)
    }

    // Draw name (bold)
    if (userName) {
      currentPdfY -= lineHeight
      page.drawText(userName, {
        x: pdfX,
        y: currentPdfY,
        size: textFontSize,
        color: rgb(0, 0, 0)
      })
    }

    // Draw title
    if (userTitle) {
      currentPdfY -= lineHeight
      page.drawText(userTitle, {
        x: pdfX,
        y: currentPdfY,
        size: textFontSize,
        color: rgb(0, 0, 0)
      })
    }

    // Draw surveyor ID
    if (surveyorId) {
      currentPdfY -= lineHeight
      page.drawText(`Surveyor ID: ${surveyorId}`, {
        x: pdfX,
        y: currentPdfY,
        size: textFontSize,
        color: rgb(0, 0, 0)
      })
    }
  }

  /**
   * Helper to draw a signature line
   */
  drawSignatureLine(page, x, y, width, height) {
    const lineY = y - height + (5 / 1.5)
    page.drawLine({
      start: { x, y: lineY },
      end: { x: x + width, y: lineY },
      thickness: 1,
      color: rgb(0, 0, 0)
    })
  }

  /**
   * Draw stamp annotation
   */
  drawStamp(page, data, pageHeight, scale) {
    const {
      x, y, width, height, color, stampText
    } = data

    // Transform coordinates
    const pdfX = x / scale
    const pdfY = pageHeight - ((y + height) / scale)
    const pdfWidth = (width || 120) / scale
    const pdfHeight = (height || 40) / scale

    const stampColor = this.colorStringToRgb(color || "red")

    // Draw background rectangle (white with slight transparency)
    page.drawRectangle({
      x: pdfX,
      y: pdfY,
      width: pdfWidth,
      height: pdfHeight,
      color: rgb(1, 1, 1),
      opacity: 0.9,
      borderColor: stampColor,
      borderWidth: 2 / scale
    })

    // Draw text centered in the stamp
    const text = stampText || "STAMP"
    const fontSize = 14 / scale

    try {
      // Calculate text position (centered)
      const textWidth = text.length * fontSize * 0.6 // Approximate text width
      const textX = pdfX + (pdfWidth - textWidth) / 2
      const textY = pdfY + (pdfHeight - fontSize) / 2

      page.drawText(text, {
        x: textX,
        y: textY,
        size: fontSize,
        color: stampColor
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to draw stamp text:", err)
    }
  }

  drawLineShape(page, data, pageHeight, scale) {
    if (!data?.start || !data?.end) {
      return
    }

    const strokeColor = this.colorStringToRgb(data.strokeColor || "#c62828")
    const thickness = (data.strokeWidth || 2) / scale
    const start = {
      x: data.start.x / scale,
      y: pageHeight - (data.start.y / scale)
    }
    const end = {
      x: data.end.x / scale,
      y: pageHeight - (data.end.y / scale)
    }

    page.drawLine({
      start,
      end,
      thickness,
      color: strokeColor
    })
  }

  drawRectangleShape(page, data, pageHeight, scale) {
    if (typeof data?.x !== "number" || typeof data?.width !== "number" || typeof data?.height !== "number") {
      return
    }

    const pdfX = data.x / scale
    const pdfY = pageHeight - ((data.y + data.height) / scale)
    const pdfWidth = data.width / scale
    const pdfHeight = data.height / scale
    const strokeColor = this.colorStringToRgb(data.strokeColor || "#c62828")
    const borderWidth = (data.strokeWidth || 2) / scale
    const fillOpacity = typeof data.fillOpacity === "number" ? data.fillOpacity : 0.2

    if (data.fillEnabled) {
      page.drawRectangle({
        x: pdfX,
        y: pdfY,
        width: pdfWidth,
        height: pdfHeight,
        color: this.colorStringToRgb(data.fillColor || data.strokeColor || "#c62828"),
        opacity: fillOpacity
      })
    }

    page.drawRectangle({
      x: pdfX,
      y: pdfY,
      width: pdfWidth,
      height: pdfHeight,
      borderColor: strokeColor,
      borderWidth
    })
  }

  drawEllipseShape(page, data, pageHeight, scale) {
    if (typeof data?.x !== "number" || typeof data?.width !== "number" || typeof data?.height !== "number") {
      return
    }

    const centerX = (data.x + (data.width / 2)) / scale
    const centerY = pageHeight - ((data.y + (data.height / 2)) / scale)
    const radiusX = Math.max((data.width / 2) / scale, 0.25)
    const radiusY = Math.max((data.height / 2) / scale, 0.25)
    const strokeColor = this.colorStringToRgb(data.strokeColor || "#1565c0")
    const borderWidth = (data.strokeWidth || 2) / scale
    const fillOpacity = typeof data.fillOpacity === "number" ? data.fillOpacity : 0.2

    if (data.fillEnabled) {
      page.drawEllipse({
        x: centerX,
        y: centerY,
        xScale: radiusX,
        yScale: radiusY,
        color: this.colorStringToRgb(data.fillColor || data.strokeColor || "#1565c0"),
        opacity: fillOpacity
      })
    }

    page.drawEllipse({
      x: centerX,
      y: centerY,
      xScale: radiusX,
      yScale: radiusY,
      borderColor: strokeColor,
      borderWidth
    })
  }

  /**
   * Convert CSS color names to pdf-lib rgb() values
   */
  colorStringToRgb(colorString) {
    if (!colorString) {
      return rgb(0, 0, 0)
    }

    const normalized = colorString.toString().trim().toLowerCase()

    const colorMap = {
      yellow: rgb(1, 1, 0),
      red: rgb(1, 0, 0),
      blue: rgb(0, 0, 1),
      green: rgb(0, 0.5, 0),
      orange: rgb(1, 0.647, 0),
      black: rgb(0, 0, 0),
      white: rgb(1, 1, 1),
      purple: rgb(0.5, 0, 0.5),
      pink: rgb(1, 0.752, 0.796),
      cyan: rgb(0, 1, 1),
      teal: rgb(0, 0.5, 0.5),
      gray: rgb(0.5, 0.5, 0.5),
      grey: rgb(0.5, 0.5, 0.5)
    }

    if (colorMap[normalized]) {
      return colorMap[normalized]
    }

    if (normalized.startsWith("#")) {
      return this.hexToRgb(normalized)
    }

    if (normalized.startsWith("rgb")) {
      return this.cssRgbStringToRgb(normalized)
    }

    return rgb(0, 0, 0)
  }

  hexToRgb(hex) {
    const sanitized = hex.replace("#", "")
    if (![3, 6].includes(sanitized.length)) {
      return rgb(0, 0, 0)
    }

    const expanded = sanitized.length === 3
      ? sanitized.split("").map(ch => ch + ch).join("")
      : sanitized

    const r = parseInt(expanded.substring(0, 2), 16) / 255
    const g = parseInt(expanded.substring(2, 4), 16) / 255
    const b = parseInt(expanded.substring(4, 6), 16) / 255

    if ([r, g, b].some(Number.isNaN)) {
      return rgb(0, 0, 0)
    }

    return rgb(r, g, b)
  }

  cssRgbStringToRgb(value) {
    const match = value.match(/rgba?\(([^)]+)\)/)
    if (!match) {
      return rgb(0, 0, 0)
    }

    const parts = match[1]
      .split(",")
      .map(part => part.trim())
      .map(Number)

    if (parts.length < 3 || parts.slice(0, 3).some(Number.isNaN)) {
      return rgb(0, 0, 0)
    }

    return rgb(parts[0] / 255, parts[1] / 255, parts[2] / 255)
  }

  /**
   * Generate export filename with suffix
   */
  generateExportFilename(originalFilename) {
    if (!originalFilename) {
      return "annotated_feedback.pdf"
    }

    const nameParts = originalFilename.split(".")
    const extension = nameParts.length > 1 ? nameParts.pop() : "pdf"
    const baseName = nameParts.join(".")

    return `${baseName}_feedback.${extension}`
  }
}

// Export singleton instance
export default new PdfExportService()
